import React, { useCallback, useEffect } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { useParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Canvas } from '../components/canvas/Canvas';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { generateCrewAICode } from '../lib/codeGenerator';
import { testFlow } from '../lib/testFlow';
import { CodePreviewModal } from '../components/CodePreviewModal';
import { useEditorStore } from '../lib/editorStore';

export function Editor() {
  const { projectId, workflowId } = useParams();
  const [showCodePreview, setShowCodePreview] = React.useState(false);
  const [generatedCode, setGeneratedCode] = React.useState('');
  const [testResults, setTestResults] = React.useState<any[] | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    updateNodeData,
    setSelectedNode,
    pushToHistory,
    loadWorkflow,
    saveWorkflow,
    isSaving,
    lastSavedAt
  } = useEditorStore();

  // Load workflow on mount
  useEffect(() => {
    if (workflowId) {
      loadWorkflow(workflowId);
    }
  }, [workflowId, loadWorkflow]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (workflowId && !isSaving && nodes.length > 0) {
        saveWorkflow();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [workflowId, isSaving, nodes, saveWorkflow]);

  const handleTestFlow = useCallback(async () => {
    if (nodes.length === 0) {
      alert('Please add some nodes to test the flow');
      return;
    }
    const results = await testFlow(nodes, edges);
    setTestResults(results.results);
  }, [nodes, edges]);

  const handleGenerateCode = useCallback(() => {
    if (nodes.length === 0) {
      alert('Please add some nodes to generate code');
      return;
    }
    const code = generateCrewAICode({ nodes, edges });
    setGeneratedCode(code);
    setShowCodePreview(true);
  }, [nodes, edges]);

  const handleSaveFlow = useCallback(async () => {
    try {
      await saveWorkflow();
    } catch (error) {
      console.error('Error saving flow:', error);
      alert('Failed to save flow. Please try again.');
    }
  }, [saveWorkflow]);

  const handleClearCanvas = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the canvas? This action cannot be undone.')) {
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
    }
  }, [setNodes, setEdges, setSelectedNode]);

  const handleNodePropertyChange = useCallback(
    (nodeId: string, property: string, value: any) => {
      updateNodeData(nodeId, { [property]: value });
      pushToHistory();
    },
    [updateNodeData, pushToHistory]
  );

  const handleNodeLabelChange = useCallback(
    (nodeId: string, label: string) => {
      updateNodeData(nodeId, { label });
      pushToHistory();
    },
    [updateNodeData, pushToHistory]
  );

  return (
    <div className="flex h-screen bg-gray-900">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      
      <main className="flex-1 flex flex-col">
        <div className="flex-1 relative">
          <ReactFlowProvider>
            <Canvas 
              testResults={testResults}
              onCloseTestResults={() => setTestResults(null)}
              onNodePropertyChange={handleNodePropertyChange}
              onNodeLabelChange={handleNodeLabelChange}
              onTestFlow={handleTestFlow}
              onGenerateCode={handleGenerateCode}
              onSaveFlow={handleSaveFlow}
              onClearCanvas={handleClearCanvas}
              isSaving={isSaving}
              lastSavedAt={lastSavedAt}
              showVersionHistory={showVersionHistory}
              onToggleVersionHistory={() => setShowVersionHistory(!showVersionHistory)}
            />
          </ReactFlowProvider>

          <VersionHistoryPanel 
            isOpen={showVersionHistory}
            onClose={() => setShowVersionHistory(false)}
          />
        </div>
      </main>

      {showCodePreview && (
        <CodePreviewModal
          code={generatedCode}
          onClose={() => setShowCodePreview(false)}
          onDownload={() => {
            const blob = new Blob([generatedCode], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'crewai-flow.py';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
        />
      )}
    </div>
  );
}
